import React, { useContext } from "react";
import axios from "axios";
import { styled } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import { useSessionId, ThemeContext, useAuthProvider, saveAttachment, getAttachment } from "@cloudpremise/portal_core";
import MDBox from "@cloudpremise/portal_core/src/themes/mui/components/MDBox";
import DashboardLayout from "@cloudpremise/portal_core/src/themes/mui/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "@cloudpremise/portal_core/src/themes/mui/examples/Navbars/DashboardNavbar";
import MDInput from "@cloudpremise/portal_core/src/themes/mui/components/MDInput";
import MDButton from "@cloudpremise/portal_core/src/themes/mui/components/MDButton";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});


const Attachments = (props) => {
    const [ authorized, authType ] = useAuthProvider();
    const navigate = useNavigate();
    const MAX_FILE_SIZE = 4500000; //Max file size 4.5 MB 
    const CHUNK_SIZE = 750000; //Chunk Max size 750Kb 
    let defaultState = {
        parentId: "",
        files: null,
        cancelToken: null,
        attachment: null,
        attachmentId: "",
        attachmentBody: null,
        attachmentLiveBody: null
    };

    const [state, setState] = React.useReducer((state: any, newState: Partial<any>) => ({
        ...state,
        ...newState
    }), defaultState);
    React.useEffect(() => {
        if(authorized && (authType !== "salesforce")){
            return navigate("/auth-not-supported", { replace: true });
        }
    });
    function handleChange(event, name){
        setState({
            [name]: event.target.value,
        });
	}

    async function handleFileChange(event, name){
        const file = event.target.files[0];
        let fileContents = await toBase64(file);
        setState({
            [name]: event.target.files,
            attachmentBody: fileContents
        });
	}

    function toBase64(file){
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    }

    async function handleSubmit(){
        const { parentId, files } = state;
        if(files === null || files.length <= 0){
            return;
        }
        const file = files[0];
        let fileContents = await toBase64(file);
        const requestData = {
            Body: fileContents,
            ContentType: file.type,
            Name: file.name,
            ParentId: parentId,
            IsPrivate: false,
            Description: file.name,
        };
        var base64 = 'base64,';
        if(typeof(fileContents) === "string"){
            var dataStart = fileContents.indexOf(base64) + base64.length;
            fileContents = fileContents.substring(dataStart);
        }
        uploadProcess(file, fileContents);

        setState({
            loading: true,
            attachment: requestData
        });
    }

    function uploadProcess(file, fileContents) {
        if (file.size > MAX_FILE_SIZE) {
            return;
        }
        // set a default size or startpostiton as 0 
        var startPosition = 0;
        // calculate the end size or endPostion using Math.min() function which is return the min. value   
        var endPosition = Math.min(fileContents.length, startPosition + CHUNK_SIZE);

        // start with the initial chunk, and set the attachId(last parameter)is null in begin
        uploadInChunk(file, fileContents, startPosition, endPosition, '');
    }

    function uploadInChunk(file, fileContents, startPosition, endPosition, attachId = '') {
        var getchunk = fileContents.substring(startPosition, endPosition);
        const { parentId } = state;
        saveAttachment(parentId, file.name, encodeURIComponent(getchunk), file.type, attachId, (result) => {
            console.log(result);
            attachId = result;
            startPosition = endPosition;
            endPosition = Math.min(fileContents.length, startPosition + CHUNK_SIZE);
            if (startPosition < endPosition) {
                uploadInChunk(file, fileContents, startPosition, endPosition, attachId);
            }else{
                setState({
                    loading: false,
                    attachmentId: attachId,
                    files: null
                });
            }
        });        
    }

    async function handleGetAttachment(attachmentId, downloadFile = false, render = false){
        if(attachmentId === null || attachmentId.length <= 0){
            return;
        }
        getAttachment(attachmentId, async (data) => {
            try{
                const result = JSON.parse(data);
                const byteCharacters = atob(result.Body);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                var blob = new Blob([byteArray]);
                const fileContents = await toBase64(blob);

                setState({
                    loading: false,
                    cancelToken: null,
                    attachment: result,
                    attachmentLiveBody: (render ? fileContents : state.attachmentLiveBody)
                });

                if(downloadFile){
                    download(fileContents, result);
                }
            }catch(e){
                console.log(e);
            }
            
        });
        const cancelToken = axios.CancelToken.source();
        setState({
            loading: true,
            cancelToken: cancelToken
        });
    }
    function download(attachmentLiveBody = null, attachment = null){
        if(attachmentLiveBody === null){
            attachmentLiveBody = state.attachmentBody;
        }
        if(attachment === null){
            attachmentLiveBody = state.attachment;
        }
        if(attachment === null || attachmentLiveBody === null){
            return handleGetAttachment(state.attachmentId, true);
        }
        var a = document.createElement("a");
        document.body.appendChild(a);
        a.href = attachmentLiveBody;
        a.target = "_blank";
        a.download = attachment.Name;
        a.click();
    }
    function isImage(){
        const { attachment, files } = state;
        if(attachment === null){
            if(files === null){
                return false;
            }
            const file = files[0];
            return (file.type.indexOf("image") !== -1);
        }
        const extension = attachment.ContentType.toLowerCase();
        return (extension.indexOf("image") !== -1);
    }
    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox my={3}>
                <Grid container justifyContent="flex-start" alignItems="flex-start">
                    <Grid item xs={12} md={12} lg={12} mr="auto" position="relative">
                        <MDBox mr={2}>
                            <MDInput 
                                label="Salesforce Id"
                                onChange={(event) => handleChange(event, "parentId")}
                                value={state.parentId}
                                sx={{width: "100%"}}
                            />
                        </MDBox>
                    </Grid>
                </Grid>
            </MDBox>
            <MDBox my={3}>
                <Grid container justifyContent="flex-start" alignItems="flex-start">
                    <Grid item xs={12} md={6} lg={6} mr="auto" position="relative">
                        <MDBox mr={2}>
                            <MDButton
                                component="label"
                                variant="outlined"
                                color="info"
                                tabIndex={-1}
                                role={undefined}
                                startIcon={<FileUploadOutlinedIcon />}
                                sx={{marginRight: "8px"}}
                            >
                                Select Files
                                <VisuallyHiddenInput type="file" onChange={(event) => handleFileChange(event, "files")} />
                            </MDButton>
                            <span className="slds-file-selector__text">
                                {
                                    state.files !== null ?
                                        state.files[0].name
                                    :
                                        ""
                                }
                            </span>
                        </MDBox>
                        <MDButton
                            variant="gradient"
                            color="info"
                            sx={{marginTop: "16px"}}
                            onClick={() => handleSubmit()}
                            disabled={(state.files === null || state.files.length === 0)}
                        >
                            Upload
                        </MDButton>
                        {
                            state.attachmentBody !== null && isImage() ?
                                <div>
                                    <h3 className="slds-m-top_medium slds-m-bottom_medium">Loaded from State</h3>
                                    <img src={state.attachmentBody} alt="attachment" style={{width: "calc(100% - 16px)"}} />
                                </div>
                            :
                            null
                        }
                    </Grid>
                    <Grid item xs={12} md={6} lg={6} mr="auto" position="relative">
                        <MDBox mr={2} mb={2}>
                            <MDInput 
                                label="Attachment Id"
                                onChange={(event) => handleChange(event, "attachmentId")}
                                value={state.attachmentId}
                                sx={{width: "100%"}}
                            />
                        </MDBox>
                        <MDBox>
                            <MDButton
                                variant="gradient"
                                color="info"
                                onClick={() => download()}
                                disabled={state.attachmentId.length === 0 && state.attachmentLiveBody === null}
                                sx={{marginRight: "16px"}}
                            >
                                Download
                            </MDButton>
                            <MDButton
                                variant="outlined"
                                color="info"
                                onClick={() => handleGetAttachment(state.attachmentId, false, true)}
                                disabled={state.attachmentId.length === 0}
                            >
                                Render
                            </MDButton>
                            {
                                state.attachmentLiveBody !== null && isImage() ?
                                    <div>
                                        <h3 className="slds-m-top_medium slds-m-bottom_medium">Loaded from Controller</h3>
                                        <img src={state.attachmentLiveBody} alt="attachment" style={{width: "calc(100% - 16px)"}} />
                                    </div>
                                :
                                null
                            }
                        </MDBox>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    )
};

export default Attachments;